import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CSChallengeCycle, CSChallengeCycleMetric} from 'src/app/core/model/project';
import {getColorForName} from 'src/app/shared/utils/name-to-color.util';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {CsChartDetailDialogHeaderComponent} from 'src/app/current-sked/challenge/shared/cs-chart-detail-dialog-header/cs-chart-detail-dialog-header.component';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {normalizeMetricValues} from '../../shared/cycle-metric.util';
import {MatTooltipModule} from '@angular/material/tooltip';

import {formatDuration} from 'src/app/shared/utils/duration.util';

export interface CsCycleChartDetailDialogData {
  cycle: CSChallengeCycle;
  metric: CSChallengeCycleMetric;
}

@Component({
  selector: 'cs-cycle-chart-detail-dialog',
  standalone: true,
  imports: [OphBackButtonComponent, CsChartDetailDialogHeaderComponent, MatTooltipModule],
  templateUrl: './cs-cycle-chart-detail-dialog.component.html',
  styleUrl: './cs-cycle-chart-detail-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsCycleChartDetailDialogComponent {
  readonly dialogRef = inject(MatDialogRef<CsCycleChartDetailDialogComponent>);
  readonly data = inject<CsCycleChartDetailDialogData>(MAT_DIALOG_DATA);

  readonly isPerDay = computed(() => this.data.metric.metricPeriod === 'perDay');

  /** Rows to show: first the clicked chart (current cycle metric), then history entries for this metric. */
  readonly historyRows = computed(() => {
    const {cycle, metric} = this.data;
    if (!metric) return [];
    const fromHistory =
      cycle?.history?.filter((h): h is CSChallengeCycleMetric => h != null && h.metricId === metric.metricId) ?? [];
    return [metric, ...fromHistory];
  });

  /** Rows with precomputed normalized + tooltip values for template (avoids function calls in template). */
  readonly historyRowsWithNormalized = computed(() =>
    this.historyRows().map(row => ({
      row,
      normalizedValues: normalizeMetricValues(row.metricValues, row.targetValue, row.metricPeriod),
      tooltipValues: (row.metricValues ?? []).map(v => {
        const formatted = this.formatMetricValue(v, row.metricType);
        if (row.metricPeriod === 'perDay') {
          const target = this.formatMetricValue(row.targetValue, row.metricType);
          const base = `${formatted}/${target}`;
          return `${base}${this.getUomSuffix(row.metricType, row.uom, Number(v))}`;
        }
        return `${formatted}${this.getUomSuffix(row.metricType, row.uom, Number(v))}`;
      }),
    }))
  );

  readonly percentComplete = computed(() => Math.min(Math.round(this.data.metric.percentComplete ?? 0), 100));

  readonly metricProgressColor = computed(() => getColorForName(this.data.metric.metricId, 'metric'));

  /** Formatted current value for the header. */
  readonly currentValueDisplay = computed(() => {
    if (this.isPerDay()) {
      return '' + this.getSuccessCount();
    }
    return '' + this.formatMetricValue(this.getSummedValue(), this.data.metric.metricType);
  });

  /** Formatted target value for the header, with UOM appended. */
  readonly targetValueDisplay = computed(() => {
    const {metricType, uom, metricPeriod} = this.data.metric;
    let base: string;
    if (this.isPerDay()) {
      base = '' + this.data.metric.targetSuccessThreshold;
    } else {
      base = '' + this.formatMetricValue(this.data.metric.targetValue, metricType);
    }
    if (metricType === TrackerMetricTypes.Duration && metricPeriod === 'perCycle') return base;
    if (metricType === TrackerMetricTypes.Percentage && metricPeriod === 'perCycle') return `${base}%`;
    if (metricType === TrackerMetricTypes.Boolean) {
      if (this.isPerDay()) {
        return `${base} days`;
      }
      return `${base} times`;
    }
    if (this.isPerDay()) {
      return `${base} days`;
    }
    return uom ? `${base} ${uom}` : base;
  });

  /** Count of metricValues that meet or exceed targetValue. Used for perDay metrics. */
  private getSuccessCount(): number {
    const vals = this.data.metric.metricValues ?? [];
    const target = this.data.metric.targetValue;
    return vals.filter(v => v >= target).length;
  }

  /** Sum of all metricValues. Used for perCycle metrics. */
  private getSummedValue(): number {
    return (this.data.metric.metricValues ?? []).reduce((a, b) => a + b, 0);
  }

  private formatMetricValue(value: string | number, type: string): string | number {
    if (type === 'duration') {
      return formatDuration(Number(value));
    }
    return value;
  }

  private getUomSuffix(metricType: TrackerMetricTypes, uom: string, value: number): string {
    if (metricType === TrackerMetricTypes.Duration) return '';
    if (metricType === TrackerMetricTypes.Percentage) return '%';
    if (metricType === TrackerMetricTypes.Boolean) {
      return value === 1 ? ' time' : ' times';
    }
    return uom ? ` ${uom}` : '';
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
